import {
  VTextField,
  VTextarea,
  VSelect,
  VAutocomplete,
  VSwitch,
} from 'vuetify/components';

import DynamicJsonField from '@/components/form/DynamicJsonField.vue'
import ProductSelector from '@/components/form/ProductSelector.vue'
import FileUploader from '@/components/form/FileUploader.vue';
import DatePickerInput from '@/components/form/DatePickerInput.vue';

import type { FormField } from '@/types/form';

export const CREATE_FORM: FormField[] = [  
  { value: 'user_id', label: 'Vendedor', component: VAutocomplete, required: true, col: 6, fetchOptions: 'searchUsers', itemText: 'name', itemValue: 'id'},
  { value: 'subject', 
    label: 'Cliente/Lead', 
    component: VAutocomplete, 
    required: true, 
    col: 6, 
    mdcol:12, 
    fetchOptions: 'searchClientsAndLeads', 
    itemText: (item: any) => item?.attributes?.commercial_name || item?.attributes?.name || '', 
    itemValue: (item: any) => item,
    transform: (value: any) => ({
      subject_id: value?.id ?? value,
      subject_type: value?.type === 'leads' ? 'Lead' : 'Client',
    })
  },
  { value: 'campaign', label: 'Campaña', component: VTextField, required: false, col: 6, mdcol:12 },
  {
    value: 'opportunity_id',
    label: 'Oportunidad',
    component: VAutocomplete,
    required: false,
    col: 6,
    fetchOptions: 'searchOpportunity',
    itemText: 'opportunity_name',
    itemValue: 'id',
    dependsOn: 'subject',
    dependsBehavior: 'disabled',
    dependsCondition: { value: null, condition: 'diferent' },
    fetchParams: (form: any) => ({
      'filter[subject_id]': form?.subject.id || null
    })
  },
  {
    value: 'quotation_items',
    label: 'Productos',
    isJsonArray: true,
    component: DynamicJsonField,
    col: 12,
    transform: (items: any[]) =>
      items.map(item => ({
        quantity: Number(item.quantity) || 1,
        quoted_item_type: item.productable_type || 'Product',
        quoted_item_id: item.productable_type === 'Product' ? item.item_id : item.productable_id,
        price: item.unit_price,
      })
    ),
    children: [
      {
        value: 'quantity',
        label: 'Cantidad',
        component: VTextField,
        type: 'number',
        required: true,
        col: 2,
      },
      {
        value: 'product_selector',
        label: 'Producto',
        component: ProductSelector,
        col: 10,
        isCustom: true,
        required: true,
        props: (row: Record<string, any>, form: Record<string, any>) => ({
          subjectId: form?.subject.id,
        }),

      }
    ],
    summary: {
      show: true,
      fields: ['subtotal'],
      formatter: (items) => {
        const total = items.reduce((acc, item) => {
          const q = parseFloat(item.quantity) || 0;
          const p = parseFloat(item.unit_price) || 0;
          item.subtotal = q * p;
          return acc + item.subtotal;
        }, 0);
        return { total };
      },
    },
  },
  {
    value: 'notes',
    label: 'Notas',
    component: VTextarea,
  },
  {
    value: 'additional_data',
    label: 'Documentos',
    isJsonArray: true,
    plainJson: false,
    component: DynamicJsonField,
    col: 12,
    children: [
      {
        value: 'label',
        label: 'Etiqueta',
        component: VTextField,
        col: 6,
        required: false,
      },
      {
        value: 'file',
        label: 'Archivo',
        component: FileUploader,
        module: 'activities', // importante
        col: 6,
        required: false,
      }
    ],
  },
];

const idFieldMappings: Record<string, string> = {
  user_id: 'user',
  opportunity_id: 'opportunity'
};

export const EDIT_FORM: FormField[] = [...CREATE_FORM.map(field => {
  if (field.value === 'quotation_items') {
    return {
      ...field,
      parse: (items: any[]) => items.map((item: any) => {
        const quoted = item.quoted_item || {};
        const isProduct = quoted.type === 'products' || quoted.productable_type === 'Product';
        const product_description = quoted?.attributes?.description;
        return {
          quantity: item.quantity,
          unit_price: item.price ?? item.unit_price,
          productable_type: quoted.attributes.productable_type ?? 'Product',
          productable_id: quoted.id ?? null,
          item_id: isProduct ? quoted.id : null,
          productables: isProduct ? [] : [quoted],
          product_description: product_description ?? null
        };

      }),
    }
  }
  const relatedKey = idFieldMappings[field.value];
  if (relatedKey) {
    return {
      ...field,
      parse: (_: any, item: any) => item[relatedKey] || null,
      transform: (val: any) => ({
        [field.value]: val?.id ?? val
      })
    };
  }
  return field;
})];

export const HEADERS = [
  { title: 'ID', value: 'id', format: 'text', sortable: true },
  { title: 'Cliente', value: 'subject', format: 'text', sortable: false },
  { title: 'Campaña', value: 'campaign', format: 'text', sortable: true },
  { title: 'Subtotal', value: 'subtotal', format: 'currency', sortable: true },
  { title: 'IVA', value: 'iva', format: 'currency', sortable: true },
  { title: 'Total', value: 'total', format: 'currency', sortable: true },
  { title: 'Autorización', value: 'needs_authorization', format: 'text', sortable: true },
  { title: 'Vendedor', value: 'user', format: 'text', sortable: false },
  { title: 'Creación', value: 'created_at', format: 'text', sortable: true },

];

export const EXPAND_HEADERS = [
  { title: 'Producto', value: 'product', format: 'text' },
  { title: 'Cantidad', value: 'quantity', format: 'decimal' },
  { title: 'Precio unitario', value: 'unit_price', format: 'currency' },
  { title: 'Total', value: 'price_with_iva', format: 'currency' },
];

export const DEFAULT_HEADER_ORDER = [
  'id',
  'subject',
  'campaign',
  'subtotal',
  'iva',
  'total',
  'needs_authorization',
  'user',
  'created_at',
];

export const DETAIL = {
  expand_table: true,
  eye_detail: false,
  key: 'quotation_items',
  component: () => import('@/components/modules/quotations/Detail.vue')
};

export const FILTERS = [
  { value: 'id', label: 'ID', component: VTextField },
  { value: 'needs_authorization', label: 'Necesita Autorización', component: VSelect, options: [
      { label: 'Sí', value: true },
      { label: 'No', value: false },
    ],
    itemText: 'label',
    itemValue: 'value',
  },
  { value: 'subject', 
    label: 'Cliente/Lead', 
    component: VAutocomplete, 
    fetchOptions: 'searchClientsAndLeads', 
    itemText: (item: any) => item?.attributes?.commercial_name || item?.attributes?.name || '', 
    itemValue: (item: any) => item,
    transform: (value: any) => ({
      subject_id: value?.id ?? value,
      subject: value?.type === 'leads' ? 'Lead' : 'Client',
    })
  },
  { value: 'user_id', label: 'Vendedor', component: VAutocomplete, fetchOptions: 'searchUsers', itemText: 'name', itemValue: 'id'},
  { value: 'created_between', label: 'Fecha de Creación', component: DatePickerInput, range: true },
  { value: 'product_id', label: 'Producto', component: VAutocomplete, fetchOptions: 'searchProducts', itemText: 'attributes.description', itemValue: 'id'},
  { value: 'notes', label: 'Notas', component: VTextField },
];